/**
 * Ambient audio manifest — the Stage/host-device night soundscape.
 *
 * These are STAGE-ONLY assets: the ambient engine (audio/stagePlayer.ts) layers
 * looping backgrounds + one-shot stingers under the narration TTS. Phones never
 * load or play these (they are caption-only). Vite resolves the imported URLs at
 * build time to real hashed asset URLs.
 *
 * Files (copied from the shared assets drop):
 *  - night-rain-background(-2): looping ambient rain beds (pick one per night)
 *  - night-1 / night-2:          looping texture layers (lower gain, layered on top)
 *  - wolfs:                       wolf-wake one-shot stinger
 *  - seer:                        seer-wake one-shot stinger (was "seer(loud).mp3")
 *  - wake:                        dawn wake one-shot
 *  - bg-generic:                  quiet day-ambient loop (DISCUSS/VOTE)
 */

import nightRainA from './audio/night-rain-background.mp3';
import nightRainB from './audio/night-rain-background-2.mp3';
import nightLayer1 from './audio/night-1.mp3';
import nightLayer2 from './audio/night-2.mp3';
import wolfsSfx from './audio/wolfs.mp3';
import wolvesSleepSfx from './audio/werewolf-gosleep.mp3';
import seerSfx from './audio/seer.mp3';
import wakeSfx from './audio/wake.mp3';
import bgGeneric from './audio/bg-generic.mp3';
import lobbyMusic from './audio/lobby.mp3';
import uiHoverClassic from './audio/ui-hover-classic.mp3';
import uiHoverOneNight from './audio/ui-hover-onenight.mp3';
import uiClick from './audio/ui-click.mp3';
import uiClickEmpty from './audio/ui-click-empty.mp3';
import modeBloodmoon1 from './audio/mode-bloodmoon-1.mp3';
import modeBloodmoon2 from './audio/mode-bloodmoon-2.mp3';
import modeBloodmoon3 from './audio/mode-bloodmoon-3.mp3';
import modeBloodmoon4 from './audio/mode-bloodmoon-4.mp3';
import modeOneNight1 from './audio/mode-onenight-1.mp3';
import modeOneNight2 from './audio/mode-onenight-2.mp3';
import modeOneNight3 from './audio/mode-onenight-3.mp3';
import modeOneNight4 from './audio/mode-onenight-4.mp3';

/** Looping ambient beds — one is chosen at random on entering the night. */
export const NIGHT_RAIN_BEDS: readonly string[] = [nightRainA, nightRainB];

/** Looping texture layers — one is chosen at random, layered at lower gain. */
export const NIGHT_LAYERS: readonly string[] = [nightLayer1, nightLayer2];

/** One-shot stingers, played over the never-stopping night bed. */
export const SFX = {
  /** Wolves wake (NIGHT_WOLVES entry). */
  wolves: wolfsSfx,
  /** Wolves go to sleep (NIGHT_INTERLUDE entry, over the WOLVES_SLEEP line). */
  wolvesSleep: wolvesSleepSfx,
  /** Seer wakes (NIGHT_SEER entry, after the interlude). */
  seer: seerSfx,
  /** Dawn wake (DAWN entry). */
  wake: wakeSfx,
} as const;

/** Quiet day-ambient loop for DISCUSS / VOTE. */
export const DAY_AMBIENT = bgGeneric;

/** Looping lobby music — plays on the Stage/host device while players gather. */
export const LOBBY_MUSIC = lobbyMusic;

/**
 * UI feedback one-shots (interface, not ceremony) — routed through the SFX bus
 * by stagePlayer.playUi at a deliberately low gain so they read as texture.
 *  - hoverClassic / hoverOneNight: one per mode card on the game-select screen
 *  - click:        any button / selectable element
 *  - clickEmpty:   a click that landed on nothing (quieter than `click`)
 */
export const UI_SFX = {
  hoverClassic: uiHoverClassic,
  hoverOneNight: uiHoverOneNight,
  click: uiClick,
  clickEmpty: uiClickEmpty,
} as const;

/**
 * The Ringmaster's reaction to a mode pick, keyed by game id — pre-voiced on
 * Eleven v3 (see packages/narration/scripts/generate-mode-lines.ts, which writes
 * these files). Played locally by stagePlayer on the select tap; one is chosen at
 * random. Voice only — the pick tap navigates straight to the lobby, which has
 * no caption band of its own to carry the line.
 */
export const MODE_PICK_LINES: Record<string, readonly string[]> = {
  bloodmoon: [modeBloodmoon1, modeBloodmoon2, modeBloodmoon3, modeBloodmoon4],
  onenight: [modeOneNight1, modeOneNight2, modeOneNight3, modeOneNight4],
};

// Gain levels for the ambient mix now live in tuning.json (see audio/audioTuning.ts)
// so the /studio UI can tune them live — they are no longer hard-coded here.
